import { LEDGER_URL } from '../config'
import { authToken } from './auth'
import { SHOW_SAMPLES, sampleLedgerRows } from './samples'

/**
 * Our own side of the ledger, read from Binny Track.
 *
 * Called from the browser rather than proxied through lereco-backend, and that is
 * a deliberate consequence of how it authenticates: the credential is the
 * signed-in user's own bearer token, which lives in this tab. Sending it to our
 * backend so our backend could forward it would mean lereco storing and relaying
 * other people's Vision tokens — a second copy of a live credential, for no gain.
 *
 * The trade to know about: what a person sees here is exactly what their Binny
 * account is allowed to see. lereco holds no service credential for this API and
 * so cannot show more than the user could — which is the right default for a
 * ledger, and also means two people may legitimately see different rows.
 */

/**
 * @typedef {object} LedgerRow
 * @property {string} clientCode
 * @property {string} clientName
 * @property {string} category      "SUNDRY DEBTORS (Customer)" / "…CREDITORS (Supplier)"
 * @property {'Dr'|'Cr'|null} side  which column carries the balance
 * @property {number} balancePaise  integer paise, always positive; `side` gives the direction
 * @property {boolean} pinned
 */

/**
 * One row of the API's `ledgerData`, in the shapes the rest of the app uses.
 *
 * Two conversions, both load-bearing:
 *
 *   **Rupees to paise.** The API sends rupees with decimals (`6420986.66`).
 *   Everything in lereco is integer paise, and a float that reaches a table an
 *   accountant reconciles against is a rounding argument waiting to happen.
 *
 *   **Debit/Credit to a side.** `ClosingBalance` carries the figure but not its
 *   direction, so `side` says which way it runs and the amount stays positive:
 *   "Cr ₹64,20,986" reads correctly where a bare negative leaves the reader
 *   working out whose favour it is in.
 *
 *   The side is decided by *which column `ClosingBalance` equals*, rather than by
 *   whichever of the two happens to be non-zero. Usually those agree, but on 5 of
 *   the 614 live rows **both** columns are non-zero — Diamorn carries Dr 40331891.93
 *   against Cr 7110894 — and "whichever is non-zero, debit first" would then pick a
 *   side by argument order rather than by evidence. Matching the closing balance is
 *   exact: across all 614 rows it equals one of the two every time, so there is a
 *   real answer to read instead of a tie to guess at.
 */
export function normaliseLedgerRow(row) {
  const debit = Number(row?.Debit) || 0
  const credit = Number(row?.Credit) || 0
  const closing = Number(row?.ClosingBalance) || 0

  // Rupees compared at paise precision — a float equality test on 40331891.93
  // would fail on a value that is, to the last paisa, the same number.
  const p = (n) => Math.round(n * 100)
  let side = null
  if (closing !== 0 && p(closing) === p(debit)) side = 'Dr'
  else if (closing !== 0 && p(closing) === p(credit)) side = 'Cr'
  else if (debit > 0) side = 'Dr'
  else if (credit > 0) side = 'Cr'

  const rupees = Math.abs(closing || debit || credit || 0)
  return {
    clientCode: String(row?.ClientCode ?? ''),
    clientName: String(row?.ClientName ?? ''),
    category: String(row?.Category ?? ''),
    locationName: String(row?.LocationName ?? ''),
    side,
    balancePaise: Math.round(rupees * 100),
    pinned: Boolean(row?.isPinned),
  }
}

/**
 * One page of clients with their balances.
 *
 * `sortBy` is passed through as the API defines it. Worth knowing what was
 * actually observed: `''` returns them by name, and every non-empty value tried
 * (`amount`, `name`, `ClosingBalance`, `desc`) returned the same order — largest
 * balance first. So it behaves as two modes rather than four, and this exposes it
 * that way rather than offering choices that do nothing.
 *
 * @param {object} [opts]
 * @param {number} [opts.page]   1-based
 * @param {number} [opts.limit]
 * @param {'name'|'amount'} [opts.order]
 */
async function fetchLedgerPage({ page = 1, limit = 25, order = 'name' } = {}) {
  if (!LEDGER_URL) {
    throw new Error('The ledger API is not configured — VITE_VISION_BACKEND_URL is not set.')
  }

  const token = authToken()
  if (!token) {
    /*
     * Worth failing loudly and specifically.
     *
     * A session can be perfectly valid for lereco's own API — which identifies a
     * caller by `x-user-id` — while carrying no bearer token at all. So "signed
     * in" and "able to read the ledger" are genuinely different states, and a
     * bare 401 here would send somebody to check a password that is not the
     * problem. Signing out and in again is what actually fixes it.
     */
    throw new Error('No Vision token in this session — sign out and sign in again to read the ledger.')
  }

  let res
  try {
    res = await fetch(LEDGER_URL, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ page, limit, sortBy: order === 'amount' ? 'amount' : '' }),
      // A ledger page is a report query and can be slow, but not unbounded — a
      // wedged call must not leave a spinner up with nothing to say.
      signal: AbortSignal.timeout(60000),
    })
  } catch (e) {
    throw new Error(
      e?.name === 'TimeoutError'
        ? 'The ledger took too long to load. Try again.'
        : 'Could not reach the ledger service.'
    )
  }

  const body = await res.json().catch(() => null)

  if (!res.ok) {
    // A 401 here is the Vision token, not lereco's session — said explicitly so
    // it does not read as a lereco problem.
    if (res.status === 401) throw new Error('Your Binny session has expired. Sign out and sign in again.')
    if (res.status === 403) throw new Error('Your Binny account does not have access to the ledger.')
    throw new Error(body?.message || `Could not load the ledger (${res.status}).`)
  }

  const d = body?.data ?? {}
  const rows = Array.isArray(d.ledgerData) ? d.ledgerData : []
  return {
    rows: rows.map(normaliseLedgerRow),
    total: Number(d.totalClients) || rows.length,
    page: Number(d.page) || page,
    limit: Number(d.limit) || limit,
    hasMore: (Number(d.page) || page) * (Number(d.limit) || limit) < (Number(d.totalClients) || 0),
  }
}

/** One call asks for more than the whole set, so the common case is a single request. */
const WHOLE_SET = 1000
/** A runaway guard, not a real expectation: 25 pages of 1000 is 25× the current total. */
const MAX_PAGES = 25

/**
 * Every client, in one call where the API allows it.
 *
 * The screen searches, filters and sorts over this list **locally**, and that is
 * the reason it is fetched whole rather than a page at a time. The ledger API has
 * no search of its own — `search`, `q`, `keyword`, `clientName`, `name` and
 * `filter` were each tried and every one returned the identical 614 rows, so it
 * ignores them. Searching a paged list would therefore have searched only the
 * rows already downloaded: typing a client's name would have found nothing
 * whenever they happened to sit on a page nobody had scrolled to yet. A search
 * box that silently misses two thirds of the data is worse than no search box.
 *
 * Fetching the lot is cheap enough to make that moot — the whole set is **614
 * rows, 136 KB, 0.17s**, measured against the live API. That is one request
 * smaller than the five pages a reader would have triggered by scrolling anyway.
 *
 * The loop is a fallback, not the plan. `limit: 1000` returns everything today,
 * but a server that quietly caps the page size would otherwise leave us
 * confidently showing a subset, so pages are pulled until the rows match the
 * declared total. `MAX_PAGES` stops a total that never agrees with the row count
 * from spinning forever.
 */
export async function fetchAllLedger({ limit = WHOLE_SET } = {}) {
  const first = await fetchLedgerPage({ page: 1, limit, order: 'name' })
  const rows = [...first.rows]

  // A page that comes back empty ends the walk too — without it, a total the
  // server over-reports would keep asking for pages that will never arrive.
  for (let page = 2; rows.length < first.total && page <= MAX_PAGES; page += 1) {
    const next = await fetchLedgerPage({ page, limit, order: 'name' })
    if (!next.rows.length) break
    rows.push(...next.rows)
  }

  // Sample clients ride along with the real list (see samples.js) — added
  // after the completeness check below counts only what Track sent.
  const complete = rows.length >= first.total
  if (SHOW_SAMPLES) rows.push(...sampleLedgerRows())

  return {
    rows,
    // What the server says it has, so the screen can say "614 clients" even if a
    // page were lost — and `rows.length` is what it actually holds. Reporting the
    // two separately is what makes a short fetch visible instead of silent.
    total: first.total + (SHOW_SAMPLES ? sampleLedgerRows().length : 0),
    complete,
  }
}
